import { useCallback } from 'react';
import {
  ChatApiUrls,
  ApiFileInfo,
  ChatConnectedAccountPayload,
  DifyApiResponse,
  HistoryResponse,
  FeedbackPayload,
  RenamePayload,
  StopStreamingPayload,
  ChatSession,
  Message,
  ChatFile,
} from '../types/chat';
import { ChatApiService, SendMessageParams } from '@/services/chatApiService';
import {
  compressImageClientSide,
  compressVideoClientSide,
  uploadViaAzureBlob,
  MAX_UPLOAD_BYTES,
  TARGET_MIN_BYTES,
} from '@/lib/fileCompression';

/**
 * Hook that wraps ChatApiService with file compression logic
 * The service handles API calls, while this hook handles file preprocessing
 */
export function useChatApi(service: ChatApiService) {
  const fetchDifyResponse = useCallback(
    async (
      message: string,
      conversationId?: string,
      userId?: string,
      files?: ApiFileInfo[],
      connectedAccount?: ChatConnectedAccountPayload
    ): Promise<DifyApiResponse> => {
      try {
        if (!userId) {
          console.warn("fetchDifyResponse called without a userId.");
        }

        const stream = await service.sendMessage({
          message,
          conversationId,
          userId: userId!,
          files,
          connected_account: connectedAccount,
        });

        return {
          content: "", // Initial content empty, will be populated by caller
          conversationId: conversationId || "", // Should be updated by stream data
          responseBody: stream,
        };
      } catch (error: any) {
        console.error("API error in fetchDifyResponse:", error);
        if (error.name === 'AbortError') {
          return { content: "", error: "Request timed out. Please try again." };
        }
        // Use centralized rate limit detection
        const { detectRateLimit } = await import('@/lib/rateLimit/detection');
        const { isRateLimit } = detectRateLimit(error);
        
        if (isRateLimit) {
          return { 
            content: "", 
            error: error.message || "Rate limit exceeded",
            rateLimitError: error, // Pass the full error object for rate limit handling
          };
        }
        return { content: "", conversationId: conversationId, error: `Error: ${error.message}` };
      }
    },
    [service]
  );

  const createRemoteConversation = useCallback(
    async (userId?: string): Promise<ChatSession | null> => {
      return service.createRemoteConversation(userId);
    },
    [service]
  );

  const fetchConversations = useCallback(
    async (userId: string, limit: number = 20): Promise<any> => {
      return service.fetchConversations(userId, limit);
    },
    [service]
  );

  const fetchHistory = useCallback(
    async (conversationId: string, userId?: string): Promise<HistoryResponse> => {
      return service.fetchHistory(conversationId, userId);
    },
    [service]
  );

  const renameConversation = useCallback(
    async (conversationId: string, newTitle: string, userId: string): Promise<any> => {
      return service.renameConversation(conversationId, newTitle, userId);
    },
    [service]
  );

  const deleteConversation = useCallback(
    async (conversationId: string, userId: string): Promise<boolean> => {
      return service.deleteConversation(conversationId, userId);
    },
    [service]
  );

  const uploadFile = useCallback(
    async (file: File, userId: string): Promise<ApiFileInfo> => {
      // Compress client-side if oversized image/video to avoid exceeding serverless payload limit
      let fileToSend: File = file;
      const originalSize = file.size;
      if (file.size > MAX_UPLOAD_BYTES) {
        if (file.type.startsWith('image/')) {
          const compressed = await compressImageClientSide(file);
          if (compressed) fileToSend = compressed;
        } else if (file.type.startsWith('video/')) {
          const compressed = await compressVideoClientSide(file);
          if (compressed) fileToSend = compressed;
        }
      }

      const formatBytes = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
      if (fileToSend !== file) {
        console.info('[useChatApi] File compressed before upload:', {
          originalSize: formatBytes(originalSize),
          compressedSize: formatBytes(fileToSend.size),
          originalName: file.name,
          compressedName: fileToSend.name,
          targetMin: formatBytes(TARGET_MIN_BYTES),
          maxUpload: formatBytes(MAX_UPLOAD_BYTES),
        });
      } else {
        console.info('[useChatApi] File size unchanged before upload:', {
          size: formatBytes(fileToSend.size),
          name: fileToSend.name,
          threshold: formatBytes(MAX_UPLOAD_BYTES),
        });
      }

      // If still too large and is image/video, fallback to Azure direct upload
      if (fileToSend.size > MAX_UPLOAD_BYTES && (fileToSend.type.startsWith('image/') || fileToSend.type.startsWith('video/'))) {
        try {
          console.log('[useChatApi] Using Azure blob fallback for oversized file', {
            userId,
            fileName: fileToSend.name,
            sizeBytes: fileToSend.size,
          });
          const blobUrl = await uploadViaAzureBlob(fileToSend, userId);
          // Call JSON URL path on our existing route (small payload)
          // We need to use the service's uploadUrlAsFile, but it expects a URL string
          // For now, we'll use the service's uploadFile method with a workaround
          // Actually, we should add a method to the service for this, but for now let's use the URL upload
          const urlFiles = await service.uploadUrlAsFile(blobUrl, userId);
          return urlFiles[0];
        } catch (e) {
          console.warn('[useChatApi] Azure direct upload fallback failed, trying form upload:', e);
        }
      }

      // Use service for the actual upload
      return service.uploadFile(fileToSend, userId);
    },
    [service]
  );

  const captionFile = useCallback(
    async (file: File, userId: string): Promise<{ caption: string | null, error?: string }> => {
      // Ensure caption request also stays under payload limit
      let fileToSend: File = file;
      if (file.size > MAX_UPLOAD_BYTES) {
        if (file.type.startsWith('image/')) {
          const compressed = await compressImageClientSide(file);
          if (compressed) fileToSend = compressed;
        } else if (file.type.startsWith('video/')) {
          const compressed = await compressVideoClientSide(file);
          if (compressed) fileToSend = compressed;
        }
      }

      // If still too large and is image/video, use Azure direct upload then caption by URL path
      if (fileToSend.size > MAX_UPLOAD_BYTES && (fileToSend.type.startsWith('image/') || fileToSend.type.startsWith('video/'))) {
        try {
          const blobUrl = await uploadViaAzureBlob(fileToSend, userId);
          return service.captionUrl(blobUrl, userId);
        } catch (e: any) {
          return { caption: null, error: `Azure upload failed: ${e.message || e}` };
        }
      }

      // Use service for captioning
      return service.captionFile(fileToSend, userId);
    },
    [service]
  );

  const postFeedback = useCallback(
    async (difyMessageId: string, rating: 'like' | 'dislike', userId: string): Promise<any> => {
      return service.postFeedback(difyMessageId, rating, userId);
    },
    [service]
  );

  const stopStreaming = useCallback(
    async (taskId: string, userId: string): Promise<any> => {
      return service.stopStreaming(taskId, userId);
    },
    [service]
  );

  const uploadUrlAsFile = useCallback(
    async (url: string, userId: string): Promise<ApiFileInfo[]> => {
      return service.uploadUrlAsFile(url, userId);
    },
    [service]
  );

  const captionUrl = useCallback(
    async (url: string, userId: string): Promise<{ caption: string | null, error?: string }> => {
      return service.captionUrl(url, userId);
    },
    [service]
  );

  return {
    fetchDifyResponse,
    createRemoteConversation,
    fetchConversations,
    fetchHistory,
    renameConversation,
    deleteConversation,
    uploadFile,
    captionFile,
    uploadUrlAsFile,
    captionUrl,
    postFeedback,
    stopStreaming,
  };
}
